import React, { useState, useRef, useEffect, useContext } from "react";
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Tabs,
  Tab,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import Q8Button from "../components/Q8Button";
import Q8SnackBar from "../components/Q8SnackBar";
import Q8Breadcrumbs from "../components/Q8Breadcrumbs";
import PowerBIService from "../service/powerbiService";
import { UserContext } from "../context/UserContext"; // Importa UserContext

// Importazione corretta del SDK di Power BI
// Assicurati che powerbi-client sia installato con: npm install powerbi-client --save
import * as powerbi from "powerbi-client";

function ReportCorporate() {
  // Ottieni informazioni sull'utente e ruolo dal context
  const { user, bestRole } = useContext(UserContext);

  // Verifica se l'utente è admin o validator
  const isAdminOrValidator = bestRole === "Admin" || bestRole === "Validator";

  const reportContainerRef = useRef(null);
  const [pbiReport, setPbiReport] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [activeTab, setActiveTab] = useState(0);
  const [directUrl, setDirectUrl] = useState("");

  // Definizione delle configurazioni dei report CORPORATE
  const reportConfigs = [
    {
      name: "Report Quality",
      reportId: "4be22ab1-588d-470f-955b-881f22fe5675",
      workspaceId: "3213381b-38a5-485a-a6b1-b7501ad1e92c",
      datasetId: "51d351ce-d561-493f-8c4e-40a10a06afb9",
      pageId: "9d0ca6fc9bd301231fbe",
      tenantId: "05694577-0e1d-4e56-b281-f6ca4b3fc3fe",
    },
    {
      name: "Report Consolidato",
      reportId: "454c30ff-ca13-4961-9dd3-f822ce7efc71",
      workspaceId: "b93e3e3d-ad3f-4d9d-96f9-56ccf0ce6b1f",
      datasetId: "b9f89ea9-fe07-404a-9a6e-14d7340c6907",
      pageId: "9d0ca6fc9bd301231fbe",
      tenantId: "05694577-0e1d-4e56-b281-f6ca4b3fc3fe",
    },
  ];

  // Usa la configurazione attiva in base al tab selezionato
  const currentConfig = reportConfigs[activeTab];

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    title: "",
    type: "default",
  });

  const showNotification = (message, type = "default", title = "") => {
    setSnackbar({ open: true, message, title, type });
  };

  // Gestione del cambio di tab
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    setIsLoading(true);
    setHasError(false);
    setErrorMessage("");
  };

  // Funzione per integrare il report Power BI usando il Power BI SDK
  const embedPowerBIReport = async (embedConfig) => {
    try {
      if (!reportContainerRef.current) {
        console.error("[PowerBI] Container di riferimento non trovato");
        return;
      }

      // Crea una nuova istanza di PowerBI
      const pbiService = new powerbi.service.Service(
        powerbi.factories.hpmFactory,
        powerbi.factories.wpmpFactory,
        powerbi.factories.routerFactory
      );

      // Pulisci il container prima di incorporare un nuovo report
      pbiService.reset(reportContainerRef.current);

      // Configura le opzioni di embedding
      const embedOptions = {
        type: "report",
        id: embedConfig.reportId,
        embedUrl: embedConfig.embedUrl,
        accessToken: embedConfig.accessToken,
        tokenType: powerbi.models.TokenType.Embed,
        permissions: powerbi.models.Permissions.Read,
        viewMode: powerbi.models.ViewMode.View,
        settings: {
          navContentPaneEnabled: false,
          filterPaneEnabled: true,
          background: powerbi.models.BackgroundType.Transparent,
          localeSettings: {
            language: "it-IT",
            formatLocale: "it-IT",
          },
        },
      };


      // Embed del report nel container
      const report = pbiService.embed(reportContainerRef.current, embedOptions);

      // Aggiungi i listener per gli eventi
      report.on("loaded", () => {
        setIsLoading(false);
      });

      report.on("error", (event) => {
        setHasError(true);
        setErrorMessage(
          event.detail.message || "Errore nell'incorporare il report"
        );
        setIsLoading(false);
      });

      report.on("rendered", () => {
        setIsLoading(false);
      });

      // Salva il riferimento al report
      setPbiReport(report);
    } catch (error) {
      console.error("[PowerBI] Errore durante l'embedding del report:", error);
      setHasError(true);
      setErrorMessage(error.message || "Errore nell'incorporare il report");
      setIsLoading(false);
    }
  };

  // Effetto per caricare il report quando cambia il tab attivo
  useEffect(() => {
    let isMounted = true;
    let safetyTimeout = null;

    const loadReport = async () => {
      if (!currentConfig) return;

      setIsLoading(true);
      setHasError(false);
      setErrorMessage("");

      try {

        // Ottieni il token di embed dal backend
        const response = await PowerBIService.getReportEmbedInfo(
          currentConfig.reportId,
          currentConfig.workspaceId,
          currentConfig.pageId
        );

        // Verifica che il componente sia ancora montato
        if (!isMounted) return;

        // Salva l'URL diretto per l'apertura nel browser
        setDirectUrl(response.directUrl);

        // Embed del report usando il Power BI SDK
        await embedPowerBIReport(response);
      } catch (error) {
        console.error(
          "[PowerBI] Errore nell'ottenere le informazioni di embed:",
          error
        );
        if (isMounted) {
          setHasError(true);
          setErrorMessage(
            error.message || "Errore nell'incorporamento del report"
          );
          setIsLoading(false);
        }
      }

      // Imposta un timeout di sicurezza per nascondere il caricamento
      safetyTimeout = setTimeout(() => {
        if (isMounted) {
          setIsLoading(false);
        }
      }, 20000); // 20 secondi per dare più tempo al caricamento
    };

    loadReport();

    // Pulizia quando il componente viene smontato
    return () => {
      isMounted = false;
      if (safetyTimeout) clearTimeout(safetyTimeout);

      // Cleanup del report se esiste
      if (pbiReport && reportContainerRef.current) {
        try {
          pbiReport.off("loaded");
          pbiReport.off("error");
          pbiReport.off("rendered");
          const pbiService = new powerbi.service.Service(
            powerbi.factories.hpmFactory,
            powerbi.factories.wpmpFactory,
            powerbi.factories.routerFactory
          );
          pbiService.reset(reportContainerRef.current);
        } catch (error) {
          console.warn(
            "[PowerBI] Errore durante la pulizia del report:",
            error
          );
        }
      }
    };
  }, [activeTab]); // Dipendenza solo da activeTab per evitare rendering multipli

  // Funzione per aggiornare i dati del dataset del report corrente
  const handleRefresh = async () => {
    if (!currentConfig || !currentConfig.datasetId) {
      showNotification(
        "Impossibile aggiornare: configurazione dataset mancante",
        "error",
        "Errore"
      );
      return;
    }

    setIsRefreshing(true);
    showNotification(
      `Aggiornamento dati in corso per il dataset del report "${currentConfig.name}"...`,
      "default",
      "Informazione"
    );

    try {
      // Aggiorna il dataset tramite l'API
      await PowerBIService.refreshDatasetViaBackend(
        currentConfig.workspaceId,
        currentConfig.datasetId
      );

      showNotification(
        `Aggiornamento dataset di "${currentConfig.name}" avviato con successo. Potrebbero essere necessari alcuni minuti per completare l'aggiornamento.`,
        "success",
        "Aggiornamento avviato"
      );
    } catch (error) {
      console.error(
        "[PowerBI] Errore durante l'aggiornamento dei dati:",
        error
      );

      showNotification(
        `Si è verificato un errore durante l'aggiornamento dei dati: ${error.message}`,
        "error",
        "Errore"
      );
    } finally {
      setIsRefreshing(false);
    }
  };

  // Funzione per ricaricare solo la visualizzazione del report corrente
  const refreshCurrentReport = () => {
    // Riavvia il caricamento del report corrente
    handleTabChange(null, activeTab);
  };

  // Funzione per aprire il report direttamente in PowerBI
  const openDirectReport = () => {
    window.open(directUrl, "_blank");
  };

  return (
    <Box sx={{ height: "100vh", display: "flex", flexDirection: "column" }}>
      <Q8Breadcrumbs
        links={[
          {
            label: "Report Tech Expenses - Corporate",
            href: "/report-corporate",
          },
        ]}
        variant="primary"
      />

      <Box
        sx={{
          mt: 3,
          mb: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Typography variant="h4" sx={{ color: "primary.main" }}>
          Report Tech Expenses - Corporate
        </Typography>
        <Box sx={{ display: "flex", gap: 2 }}>
          <Q8Button
            variant="primary"
            label="Aggiorna Visualizzazione"
            icon={<RefreshIcon />}
            onClick={refreshCurrentReport}
            disabled={isLoading}
            size="small"
            tooltip="Ricarica solo la visualizzazione del report corrente"
          />
          {/* Mostra il pulsante Aggiorna Dati solo per Admin e Validator */}
          {isAdminOrValidator && (
            <Q8Button
              variant="primary"
              label={`Aggiorna Dati (${currentConfig?.name || ""})`}
              icon={<RefreshIcon />}
              onClick={handleRefresh}
              disabled={isRefreshing}
              size="small"
              tooltip={`Aggiorna il dataset del report "${
                currentConfig?.name || ""
              }" (ID: ${currentConfig?.datasetId || "N/A"})`}
            />
          )}
          <Q8Button
            variant="secondary"
            label="Apri nel Browser"
            icon={<OpenInNewIcon />}
            onClick={openDirectReport}
            size="small"
            tooltip="Apri il report direttamente in PowerBI"
          />
        </Box>
      </Box>

      {/* Tab per selezionare il report */}
      <Tabs
        value={activeTab}
        onChange={handleTabChange}
        aria-label="report tabs"
        sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}
      >
        {reportConfigs.map((config, index) => (
          <Tab
            key={index}
            label={config.name}
            sx={{
              "&.Mui-selected": { fontWeight: "bold" },
            }}
          />
        ))}
      </Tabs>

      {/* Messaggio di errore */}
      {hasError && (
        <Box
          sx={{
            my: 1,
            p: 2,
            bgcolor: "#FFEBEE",
            border: "1px solid #FFCDD2",
            borderRadius: 1,
            display: "flex",
            flexDirection: "column",
            gap: 1,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <InfoOutlinedIcon sx={{ color: "#E53935" }} />
            <Typography variant="body1" fontWeight="bold">
              Errore
            </Typography>
          </Box>

          <Typography variant="body2">{errorMessage}</Typography>

          <Typography variant="body2">
            Prova a ricaricare la pagina o ad aprire il report direttamente in
            PowerBI.
          </Typography>
        </Box>
      )}

      <Paper
        elevation={3}
        sx={{ p: 0, overflow: "hidden", flex: 1, position: "relative" }}
      >
        {isLoading && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              backgroundColor: "rgba(255, 255, 255, 0.8)",
              zIndex: 1,
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 2,
              }}
            >
              <CircularProgress />
              <Typography>
                {isRefreshing
                  ? "Aggiornamento dati in corso..."
                  : `Caricamento report "${
                      currentConfig?.name || ""
                    }" in corso...`}
              </Typography>
            </Box>
          </Box>
        )}
        <div
          ref={reportContainerRef}
          style={{
            width: "100%",
            height: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        ></div>
      </Paper>

      <Q8SnackBar
        open={snackbar.open}
        type={snackbar.type}
        title={snackbar.title}
        message={snackbar.message}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      />
    </Box>
  );
}

export default ReportCorporate;
